import React, { useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import {
  type CustomChannelMetrics,
  useCustomChannelMetrics,
} from "@/hooks/api/use-custom-channels";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { useCustomChannelSelection } from "@/hooks/use-custom-channel-selection";
import { useSelectedRulesetId } from "@/hooks/use-selected-ruleset";
import {
  DEFAULT_METRIC_ORDER,
  DRILLDOWN_LIMIT,
  HIDDEN_METRICS,
} from "@/pages/custom-channels/custom-channels-list/custom-channels-metrics-config";
import { CustomChannelMetricCards } from "./metric-cards";
import { MetricDrilldown } from "./metric-drilldown";

export function CustomChannelScorecard() {
  const { selectedChannel, selectedSubchannel } = useCustomChannelSelection();
  const selectedRulesetId = useSelectedRulesetId();
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
  } = useChannelFilterState("custom-channels::explorer");

  const [selectedMetric, setSelectedMetric] = useState<
    keyof CustomChannelMetrics | null
  >(null);

  // Use DEFAULT_METRIC_ORDER and add required hidden metrics
  const metricsToFetch = React.useMemo(
    () => [...new Set([...DEFAULT_METRIC_ORDER, ...HIDDEN_METRICS])],
    [],
  );

  // Build filter object for API calls
  const apiFilters = React.useMemo(() => {
    return {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
      ...(hasCampaignFilters && { campaign_filters: campaignFilters }),
    };
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
  ]);

  // Get custom channel metrics
  const shouldShowMetrics = !!selectedChannel;

  const { data: metrics, loadingStates } = useCustomChannelMetrics({
    channel: selectedChannel || "",
    subchannel: selectedSubchannel,
    metrics: metricsToFetch,
    filters: apiFilters,
    rule_set_id: selectedRulesetId ?? undefined,
  });

  return (
    <div className="space-y-4">
      <CustomChannelMetricCards
        metrics={metrics}
        onMetricSelect={(metric: string) => {
          setSelectedMetric(metric as keyof CustomChannelMetrics);
        }}
        loadingStates={loadingStates}
        showBody={shouldShowMetrics}
        emptyBody={
          !shouldShowMetrics ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <EmptyState
                title="No channel selected"
                description="Select a channel from the dropdown above to view metrics."
              />
            </div>
          ) : undefined
        }
      />

      {selectedMetric && (
        <MetricDrilldown
          open={!!selectedMetric}
          onOpenChange={(open) => {
            if (!open) setSelectedMetric(null);
          }}
          channel={selectedChannel || null}
          subchannel={selectedSubchannel}
          metric={selectedMetric}
          filters={apiFilters}
          metrics={metrics}
          loadingStates={loadingStates}
          drillDownLimit={DRILLDOWN_LIMIT}
          rule_set_id={selectedRulesetId ?? undefined}
        />
      )}
    </div>
  );
}
